import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useUpdateProduct } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

type ProductModelFormValues = {
  productModel: string
}

type V2ProductModelEditDialogProps = {
  productId: string
  productModel: string | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function V2ProductModelEditDialog({
  productId,
  productModel,
  open,
  onOpenChange,
}: V2ProductModelEditDialogProps) {
  const { t } = useTranslation('common')
  const { mutateAsync: updateProduct, isPending } = useUpdateProduct()

  const form = useForm<ProductModelFormValues>({
    defaultValues: {
      productModel: productModel ?? '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        productModel: productModel ?? '',
      })
    }
  }, [open, productModel, form])

  async function handleSubmit(values: ProductModelFormValues) {
    try {
      await updateProduct({
        productId,
        productModel:
          values.productModel.trim() === '' ? null : values.productModel.trim(),
      })
      onOpenChange(false)
      toast.success(t('v2.pages.products.detail.productModel.edit.saveSuccess'))
    } catch {
      toast.error(t('v2.pages.products.detail.productModel.edit.saveFailed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('v2.pages.products.detail.productModel.edit.title')}
          </DialogTitle>
          <DialogDescription>
            {t('v2.pages.products.detail.productModel.edit.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="productModel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('v2.pages.products.create.productModelLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.products.create.productModelPlaceholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => onOpenChange(false)}
              >
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                )}
                {t('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
